<script lang="ts">
  import 'simplebar';
  import 'simplebar/dist/simplebar.css';
  import Bin from '$icons/BinIcon.svelte';
  import Category from '$icons/CategoryIcon.svelte';
  import KeyboardHint from '$icons/KeyboardHintIcon.svelte';
  import TagIcon from '$icons/TagIcon.svelte';
  import Button from '$lib/components/common/buttons/Button.svelte';
  import Modal from '$lib/components/Modals/SmallModal/Modal.svelte';
  import Separator from '$lib/components/homepage/Separator.svelte';
  import {
    additionalPostFilters,
    timePostFilters,
    type FilterValidityRecord,
    getDynamicFilterConfig,
    type PostFiltersSchemaWithDynamic,
    getEmptyFiltersWithDynamic
  } from '$src/utils/api/feed.util';
  import FilterGroup from '$lib/components/homepage/filters/FilterGroup.svelte';
  import { createEventDispatcher, type ComponentProps, onMount, SvelteComponent } from 'svelte';
  import { cloneDeep, debounce } from 'lodash-es';
  import Dropdown from '$lib/components/common/dropdowns/Dropdown.svelte';
  import { DropdownItem } from '$lib/components/common/dropdowns/dropdown';
  import { createQuery } from '@tanstack/svelte-query';
  import {
    fetchCategories as fetchCategories,
    fetchCategoryByName,
    type FetchCategoriesData
  } from '$utils/api/categories.util';
  import { apiSearchSubcats, type DataApiSearchSubcats } from '$src/utils/api/subcategories.util';
  import BorderedAccordion from '$lib/components/homepage/accordions/BorderedAccordion.svelte';
  import type { ICategory, ISubCategory } from 'garden-common';

  const dispatch = createEventDispatcher<{
    applyFilters: { filters: PostFiltersSchemaWithDynamic };
    clickModalClose: {};
  }>();

  export let initialFilters: PostFiltersSchemaWithDynamic;

  let clearFilterHandlers: ComponentProps<FilterGroup>['clearAll'][] = [];

  function dispatchApplyFilters() {
    dispatch('applyFilters', { filters: cloneDeep(filters) });
  }

  // Refetch on modal close if filters were cleared
  function clearAll() {
    clearCatDropdown();
    clearSubcatDropdown();

    for (const clearHandler of clearFilterHandlers) {
      clearHandler();
    }

    filters = getEmptyFiltersWithDynamic();
  }

  // Categories logic
  let catSearchString = '';

  const catQuery = createQuery<FetchCategoriesData>({
    queryKey: ['cats'], // 😼
    queryFn: () => fetchCategories(catSearchString),
    refetchOnWindowFocus: false
  });

  const debouncedCatSearchRefetch = debounce($catQuery.refetch, 500);

  $: catSearchString, debouncedCatSearchRefetch();
  $: catDropdownItems =
    $catQuery.data?.categories.map(
      c => new DropdownItem({ variant: 'label', data: { label: c.title }, customData: c })
    ) || [];

  // Subcategories logic
  let subcatSearchString = '';
  let loading = false;
  const subcatQuery = createQuery<DataApiSearchSubcats>({
    queryKey: ['subcats'], // 😼😼 hahah :)
    queryFn: async () => {
      loading = true;
      const { subcategories } = selectedCat
        ? await apiSearchSubcats(subcatSearchString, selectedCat?.customData.title)
        : [];

      loading = false;
      return {
        subcategories
      };
    },
    initialData: <DataApiSearchSubcats>{ subcategories: [] },
    refetchOnWindowFocus: false
  });

  const debouncedSubcatRefetch = debounce($subcatQuery.refetch, 500);

  // Uncomment the following line to enable refetching subcategories
  // on user query change
  // $: subcatSearchString, selectedCat && debouncedSubcatRefetch();
  $: selectedCat && debouncedSubcatRefetch();

  function getSubcatDropdownItem(s: ISubCategory) {
    return new DropdownItem({
      id: s._id,
      variant: 'label,secondaryLabel',
      data: { label: s.name, secondaryLabel: s.categoryName },
      customData: s
    });
  }

  $: subcatDropdownItems = $subcatQuery.data.subcategories?.map(getSubcatDropdownItem) ?? [];

  let loadingCatFromPreviousData = false;
  let usedCatFromInitData = false;

  function setSelectedCatFromInitialData() {
    if (!initialFilters.categoryName || usedCatFromInitData) {
      return;
    }

    selectedCat = catDropdownItems.find(i => i.data.label === initialFilters.categoryName);
    usedCatFromInitData = true;
  }

  $: if (catDropdownItems.length) setSelectedCatFromInitialData();

  let usedSubcatsFromPrevData = false;

  $: if (
    subcatDropdownItems.length &&
    initialFilters.subcategoryNames &&
    !usedSubcatsFromPrevData
  ) {
    usedSubcatsFromPrevData = true;
  }

  let selectedCat: DropdownItem<ICategory>;
  let selectedSubcats: DropdownItem<ISubCategory>[] = [];

  $: if (selectedSubcats?.length) {
    filters.subcategoryNames = selectedSubcats.map(i => i.data.label);
  } else {
    delete filters.subcategoryNames;
    filters = filters;
  }

  // Filters object that's going to API
  export let filters: PostFiltersSchemaWithDynamic;

  function getAppliedFilterCount() {
    let nFilters = 0;

    for (const key in filters) {
      if (key !== 'dynamicFilters') {
        nFilters++;
      }
    }

    const dynamicFilters = filters.dynamicFilters || {};

    for (const key in dynamicFilters) {
      nFilters++;
    }

    return nFilters;
  }

  let nFilters = 0;

  $: {
    filters, (nFilters = getAppliedFilterCount());
  }

  let filterValidRecord: FilterValidityRecord = {};
  $: allValid = Object.values(filterValidRecord).filter(v => !v).length === 0;

  // Modal close
  function onModalClose() {
    dispatch('clickModalClose');
  }

  // Mount, use initial filters
  onMount(async () => {
    if (initialFilters.categoryName) {
      loadingCatFromPreviousData = true;

      const selectedSubCategoryData = await fetchCategoryByName(initialFilters.categoryName);
      catSearchString = selectedSubCategoryData.title;

      loadingCatFromPreviousData = false;
    }

    filters = cloneDeep(initialFilters);
  });

  function getSelectedDynamicFromCatAndSection(
    categoryName: string,
    sectionName: string,
    filters: PostFiltersSchemaWithDynamic
  ) {
    const start = categoryName + '.' + sectionName + '.';

    const res = [];

    for (const k of Object.keys(filters.dynamicFilters)) {
      if (k.startsWith(start)) {
        res.push(k);
      }
    }

    return res;
  }

  function getTotalNumberOfFilters(item: DropdownItem<ISubCategory>) {
    let total = 0;

    for (const sectionKey in item.customData.values) {
      total += item.customData.values[sectionKey].length;
    }

    return total;
  }

  const subCatToExpand: Record<string, () => void> = {};

  let clearCatDropdown: () => void;
  let clearSubcatDropdown: () => void;

  const _initialFilters = cloneDeep(initialFilters);

  $: filtersUnchanged = JSON.stringify(_initialFilters) === JSON.stringify(filters);

  function getFilterCountBySubcat(f: PostFiltersSchemaWithDynamic, subcatName: string) {
    const keys = Object.keys(f.dynamicFilters);
    console.log(f);
    const fromSubcat = keys.filter(i => i.split('.')[0] === subcatName);

    return fromSubcat.length;
  }
</script>

<Modal
  class="w-[550px] h-[700px]"
  contentOverflow
  on:ModalClose={onModalClose}
  disablePadding
  contentSlotContainerClass="mr-2"
>
  <!-- Header -->
  <div slot="header" class="px-6 pt-6">
    <div class="text-blueish-grey-900 text-lg font-bold">Filters</div>
  </div>

  <!-- Content -->
  <div
    slot="content"
    class="w-full pb-8 overflow-y-scroll h-full px-6 pt-4 scrollbar-hide"
    data-simplebar
    data-simplebar-auto-hide="false"
  >
    <div class="text-garden-grey-400">
      Each category and tag has specific filter options. Use these filters to narrow down your
      search and find the exact performance.
    </div>

    <!-- Categories -->
    <Dropdown
      variant="search"
      items={catDropdownItems}
      bind:selected={selectedCat}
      bind:query={catSearchString}
      bind:clear={clearCatDropdown}
      on:clearSelected={() => {
        if (!catSearchString) {
          selectedSubcats = [];
        }
      }}
      on:itemSelected={ev => {
        selectedSubcats = [];
        filters.categoryName = ev.detail.item.data.label;
        if (selectedCat) catSearchString = selectedCat.customData.title;
      }}
      label="Category"
      labelIcon={Category}
      filterByQuery
      showSkeleton={loadingCatFromPreviousData}
    />

    <!-- Subcategories -->
    <Dropdown
      disabled={!selectedCat}
      variant="mul-tag-search"
      items={subcatDropdownItems}
      labelIcon={TagIcon}
      label="Subcategories"
      filterByQuery
      showSkeleton={loadingCatFromPreviousData}
      {loading}
      bind:selectedItems={selectedSubcats}
      bind:query={subcatSearchString}
      bind:clear={clearSubcatDropdown}
    />

    <Separator class="mt-6" />

    <FilterGroup
      filterConfigs={additionalPostFilters}
      bind:filterDetails={filters}
      bind:clearAll={clearFilterHandlers[0]}
      bind:validity={filterValidRecord}
      selectOnMountByIds={Object.keys(filters)}
      title="Additional filters"
      class="mt-4"
    />

    <FilterGroup
      filterConfigs={timePostFilters}
      bind:filterDetails={filters}
      bind:clearAll={clearFilterHandlers[1]}
      bind:validity={filterValidRecord}
      selectOnMountByIds={Object.keys(filters)}
      title="Time filters"
      class="mt-4"
    />

    <Separator class="mt-4" />

    <!-- Advanced filters ('Advance' is not a typo) -->
    <div class="text-blueish-grey-900 font-bold mt-6">Advance filters</div>

    <div class="text-color-gray-400 text-sm font-medium">
      <div class=" flex-grow">
        Add more filters

        {#if selectedSubcats?.length}
          for
        {/if}

        <span class="text-brand-green font-bold cursor-pointer"
          >{!selectedCat?.data?.label ? '' : selectedCat.data.label}</span
        >
        {#each selectedSubcats as item, index}
          {index !== 0 && index == selectedSubcats.length - 1 ? ' & ' : ' , '}
          <span
            class="text-brand-green font-bold cursor-pointer"
            on:click={subCatToExpand[item.customData._id]}
            on:keypress={subCatToExpand[item.customData._id]}
            role="button"
            tabindex="0"
          >
            {item.customData.name}
          </span>
        {/each}

        to get refined results.
      </div>
    </div>

    <div class="mt-4 gap-y-2 flex flex-col">
      {#if selectedCat}
        <BorderedAccordion
          title={selectedCat.data.label}
          titleNumber={getFilterCountBySubcat(filters, selectedCat.data.label)}
          bind:expand={subCatToExpand[selectedCat.customData._id]}
        >
          {#each Object.entries(selectedCat.customData.values) as [sectionName, inputConfigs]}
            <FilterGroup
              filterConfigs={inputConfigs.map(filter =>
                getDynamicFilterConfig(selectedCat.data.label, sectionName, filter)
              )}
              selectOnMountByIds={getSelectedDynamicFromCatAndSection(
                selectedCat.data.label,
                sectionName,
                filters
              )}
              bind:filterDetails={filters}
              bind:clearAll={clearFilterHandlers[clearFilterHandlers.length]}
              bind:validity={filterValidRecord}
              title={sectionName}
              useAccordion={false}
              showAddCustom
              {sectionName}
              subcategoryName={selectedCat.data.label}
            />
          {/each}
        </BorderedAccordion>
      {/if}
      {#each selectedSubcats as subcatItem}
        <BorderedAccordion
          title={subcatItem.customData.name}
          titleNumber={getFilterCountBySubcat(filters, subcatItem.customData.name)}
          bind:expand={subCatToExpand[subcatItem.customData._id]}
        >
          {#each Object.entries(subcatItem.customData.values) as [sectionName, inputConfigs]}
            <FilterGroup
              filterConfigs={inputConfigs.map(filter =>
                getDynamicFilterConfig(subcatItem.customData.name, sectionName, filter)
              )}
              selectOnMountByIds={getSelectedDynamicFromCatAndSection(
                subcatItem.customData.name,
                sectionName,
                filters
              )}
              bind:filterDetails={filters}
              bind:clearAll={clearFilterHandlers[clearFilterHandlers.length]}
              bind:validity={filterValidRecord}
              title={sectionName}
              useAccordion={false}
              showAddCustom
              {sectionName}
              subcategoryName={subcatItem.customData.name}
            />
          {/each}
        </BorderedAccordion>
      {/each}
    </div>
  </div>

  <!-- Footer -->
  <div slot="footer" class="px-6 md:pb-6">
    <div class="-mx-6">
      <Separator />
    </div>

    <div class="flex items-center gap-2 mt-4">
      <div class="text-gray-400 flex-grow">{nFilters} filters applied</div>

      <Button
        secondary
        iconLeft={Bin}
        label="Clear all"
        disabled={nFilters === 0}
        on:click={clearAll}
      />
      <Button
        primary
        clickOnKey="Enter"
        iconRight={KeyboardHint}
        label="Apply"
        on:click|once={dispatchApplyFilters}
        disabled={!allValid}
      />
    </div>
  </div>
</Modal>

<style lang="postcss">
  :global(.simplebar-scrollbar::before) {
    @apply my-2 translate-x-[5px];
    background: #d0ddd7 !important;
    width: 4px;
  }

  :global(.simplebar-content-wrapper) {
    @apply overscroll-contain;
  }
</style>
